/**
 * Notification Service - Manages browser push notifications for review reminders
 */

const NOTIFICATION_SETTINGS_KEY = 'memo-fast-notification-settings'
const LAST_NOTIFICATION_KEY = 'memo-fast-last-notification'

class NotificationService {
  constructor() {
    this.settings = this.loadSettings()
    this.checkInterval = null
  }

  /**
   * Default settings
   */
  getDefaultSettings() {
    return {
      enabled: false,
      reminderTime: '09:00', // Default reminder time
      repeatDaily: true,
      soundEnabled: true,
      showPreview: true
    }
  }

  /**
   * Load settings from localStorage
   */
  loadSettings() {
    try {
      const saved = localStorage.getItem(NOTIFICATION_SETTINGS_KEY)
      if (saved) {
        return { ...this.getDefaultSettings(), ...JSON.parse(saved) }
      }
    } catch (e) {
      console.warn('Failed to load notification settings:', e)
    }
    return this.getDefaultSettings()
  }

  /**
   * Save settings to localStorage
   */
  saveSettings(settings) {
    this.settings = { ...this.settings, ...settings }
    localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(this.settings))

    if (this.settings.enabled) {
      this.startScheduler()
    } else {
      this.stopScheduler()
    }
  }

  /**
   * Get current settings
   */
  getSettings() {
    return { ...this.settings }
  }

  /**
   * Check if notifications are supported
   */
  isSupported() {
    return 'Notification' in window
  }

  /**
   * Get current permission status
   */
  getPermission() {
    if (!this.isSupported()) return 'unsupported'
    return Notification.permission
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!this.isSupported()) {
      return { success: false, reason: 'unsupported' }
    }

    if (Notification.permission === 'granted') {
      return { success: true, permission: 'granted' }
    }

    if (Notification.permission === 'denied') {
      return { success: false, reason: 'denied' }
    }

    try {
      const permission = await Notification.requestPermission()
      return {
        success: permission === 'granted',
        permission
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      return { success: false, reason: 'error', error }
    }
  }

  /**
   * Send a notification immediately
   */
  async sendNotification(title, options = {}) {
    if (!this.isSupported()) {
      console.warn('Notifications not supported')
      return false
    }

    if (Notification.permission !== 'granted') {
      console.warn('Notification permission not granted')
      return false
    }

    const defaultOptions = {
      icon: '/favicon.svg',
      badge: '/favicon.svg',
      tag: 'memo-fast-reminder',
      renotify: true,
      requireInteraction: false,
      silent: !this.settings.soundEnabled
    }

    try {
      const notification = new Notification(title, { ...defaultOptions, ...options })

      notification.onclick = () => {
        window.focus()
        notification.close()
        // Navigate to home page
        if (window.location.pathname !== '/') {
          window.location.href = '/'
        }
      }

      // Auto close after 10 seconds
      setTimeout(() => notification.close(), 10000)

      return true
    } catch (error) {
      console.error('Error sending notification:', error)
      return false
    }
  }

  /**
   * Send a review reminder notification
   */
  async sendReviewReminder(cardCount = 0) {
    const title = 'MemoFast - Rappel de révision'
    let body = 'Il est temps de réviser vos cartes !'

    if (this.settings.showPreview && cardCount > 0) {
      body = `Vous avez ${cardCount} carte${cardCount > 1 ? 's' : ''} à réviser aujourd'hui.`
    }

    return this.sendNotification(title, { body })
  }

  /**
   * Start the notification scheduler
   */
  startScheduler() {
    this.stopScheduler() // Clear any existing interval

    // Check every minute
    this.checkInterval = setInterval(() => {
      this.checkAndNotify()
    }, 60000)

    // Also check immediately
    this.checkAndNotify()

  }

  /**
   * Stop the notification scheduler
   */
  stopScheduler() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
    }
  }

  /**
   * Check if it's time to send a notification
   */
  async checkAndNotify() {
    if (!this.settings.enabled) return

    const now = new Date()
    const currentTime = now.toTimeString().slice(0, 5) // HH:MM format
    const today = now.toISOString().split('T')[0]

    // Check if it's the right time
    if (currentTime !== this.settings.reminderTime) return

    // Check if we already notified today
    const lastNotification = localStorage.getItem(LAST_NOTIFICATION_KEY)
    if (lastNotification === today) return

    // Send notification
    const sent = await this.sendReviewReminder()

    if (sent) {
      localStorage.setItem(LAST_NOTIFICATION_KEY, today)
    }
  }

  /**
   * Initialize the service
   */
  init() {
    if (this.settings.enabled && this.getPermission() === 'granted') {
      this.startScheduler()
    }
  }

  /**
   * Test notification (for settings page)
   */
  async testNotification() {
    return this.sendNotification('Test de notification', {
      body: 'Les notifications fonctionnent correctement !',
      tag: 'memo-fast-test'
    })
  }
}

// Singleton instance
const notificationService = new NotificationService()

export { notificationService }
export default notificationService
